import React, { useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container, Header, Segment, Icon, Button, Divider,
  Message, Progress, Table, Label, Checkbox, Loader, Popup
} from 'semantic-ui-react';
import { AuthStdFooter } from '../../utils/auth-std-footer';
import {
  PUBLISH_PHASES,
  ITEM_STATUS,
  fetchReadyPrototypes,
  selectPublishPhase,
  selectAvailablePrototypes,
  selectAvailableLoading,
  selectSelectedUuids,
  selectPublishQueue,
  selectPublishProgress,
  selectIsPaused,
  selectPublishError,
  togglePrototypeSelection,
  selectAllPrototypes,
  deselectAllPrototypes,
  buildPublishQueue,
  setPublishPhase,
  pausePublishing,
  resumePublishing,
  cancelPublishing,
  resetPublishing,
  runPublishPipeline,
} from '../../slices/prototypePublishSlice';
import '../../styles/landing-pages.css';
import lightening from '../../assets/lightening.svg';

// ============================================================
// Selection Screen — choose prototypes to publish
// ============================================================
const PrototypeSelectionScreen = ({ dispatch }) => {
  const allPrototypes = useSelector(selectAvailablePrototypes);
  const loading = useSelector(selectAvailableLoading);
  const selectedUuids = useSelector(selectSelectedUuids);

  // Arriving from a prototype's Review screen names that prototype. Arriving here directly
  // does not, and the page stays the batch tool it was written as.
  //
  // WHY IT MATTERS: the list is every ready draft the user owns, and nothing moves a
  // prototype OUT of 'ready' except publishing it. So a Literary Group marked ready weeks ago
  // and abandoned sits in the list forever, indistinguishable from the one just created —
  // and publishing is slow, expensive and hard to undo. Scoping to what the operator came
  // here to do removes the chance entirely, rather than relying on reading the list.
  const [searchParams] = useSearchParams();
  const focusUuid = searchParams.get('prototype');
  const prototypes = focusUuid
    ? allPrototypes.filter((p) => p && p.uuid === focusUuid)
    : allPrototypes;
  const hiddenCount = allPrototypes.length - prototypes.length;

  useEffect(() => {
    dispatch(fetchReadyPrototypes());
  }, [dispatch]);

  // Pre-select the one they came for, so the common path is a single click and "select all"
  // cannot reach past it.
  useEffect(() => {
    if (!focusUuid) return;
    if (prototypes.length === 1 && !selectedUuids.includes(focusUuid)) {
      dispatch(togglePrototypeSelection(focusUuid));
    }
  }, [dispatch, focusUuid, prototypes.length, selectedUuids]);

  const allSelected = prototypes.length > 0 && selectedUuids.length === prototypes.length;
  const someSelected = selectedUuids.length > 0;

  const handleToggleAll = () => {
    if (allSelected) {
      dispatch(deselectAllPrototypes());
    } else {
      dispatch(selectAllPrototypes());
    }
  };

  const handleProceed = () => {
    dispatch(buildPublishQueue());
    dispatch(setPublishPhase(PUBLISH_PHASES.CONFIRMING));
  };

  if (loading) {
    return (
      <Segment placeholder>
        <Loader active inline="centered">Loading ready prototypes...</Loader>
      </Segment>
    );
  }

  if (prototypes.length === 0) {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name="inbox" />
          No Groups Ready
        </Header>
        <p style={{ textAlign: 'center', color: '#666', maxWidth: '400px', margin: '0 auto' }}>
          You don't have any literary groups with "ready" status. Use the{' '}
          {/* <Link to="/landing/text-library">Prototype Library</Link> to prepare your books first. */}
          <Link to="/add-litgroups-books">Literary Group Builder</Link> to prepare your books first.
        </p>
        {/* was:         <Button as={Link} to="/landing/text-library" primary> */}
        <Button as={Link} to="/add-litgroups-books" primary>
          Go to Groups Library
        </Button>
      </Segment>
    );
  }

  return (
    <Segment>
      <Message info>
        <Message.Header>
          {focusUuid ? 'Publish This Literary Group' : 'Select Drafts to Publish'}
        </Message.Header>
        <p>
          Each selected draft will be converted into a complete Literary Group with books,
          covers, and author profiles. The PDFs already uploaded in the draft will be
          used — no need to re-upload.
        </p>
        {/* Say what is being withheld and offer the way to it. Filtering silently would
            just move the surprise: someone who genuinely wants the batch tool needs to know
            it is still there. */}
        {focusUuid && hiddenCount > 0 && (
          <p style={{ marginTop: '0.5em' }}>
            <Icon name="filter" />
            Showing only the Literary Group you came from.{' '}
            {hiddenCount} other ready draft{hiddenCount === 1 ? '' : 's'} hidden —{' '}
            <Link to="/publish-prototypes">show all</Link>.
          </p>
        )}
      </Message>

      <Table celled selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={handleToggleAll}
              />
            </Table.HeaderCell>
            <Table.HeaderCell width={4}>Name</Table.HeaderCell>
            <Table.HeaderCell width={3}>Author</Table.HeaderCell>
            <Table.HeaderCell width={2}>Genre</Table.HeaderCell>
            <Table.HeaderCell width={2}>Type</Table.HeaderCell>
            <Table.HeaderCell width={2}>Books</Table.HeaderCell>
            <Table.HeaderCell width={2}>Covers</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {prototypes.map((proto) => {
            const isSelected = selectedUuids.includes(proto.uuid);
            return (
              <Table.Row
                key={proto.uuid}
                active={isSelected}
                onClick={() => dispatch(togglePrototypeSelection(proto.uuid))}
                style={{ cursor: 'pointer' }}
              >
                <Table.Cell>
                  <Checkbox checked={isSelected} />
                </Table.Cell>
                <Table.Cell>
                  <strong>{proto.name}</strong>
                </Table.Cell>
                <Table.Cell>{proto.authorName || '—'}</Table.Cell>
                <Table.Cell>{proto.genre || '—'}</Table.Cell>
                <Table.Cell>
                  <Label size="tiny">{proto.litGroupType || 'series'}</Label>
                </Table.Cell>
                <Table.Cell>
                  <Icon name="book" /> {proto.bookCount || 0}
                </Table.Cell>
                <Table.Cell>
                  {(proto.coversCount || 0) > 0 ? (
                    <Label color="green" size="tiny">
                      <Icon name="image" /> {proto.coversCount}
                    </Label>
                  ) : (
                    <Label color="grey" size="tiny">None</Label>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <div style={{ marginTop: '1em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#666' }}>
          {selectedUuids.length} of {prototypes.length} selected
        </span>
        <Button
          primary
          disabled={!someSelected}
          onClick={handleProceed}
          icon="arrow right"
          labelPosition="right"
          content={`Publish ${selectedUuids.length} Draft${selectedUuids.length !== 1 ? 's' : ''}`}
        />
      </div>
    </Segment>
  );
};

// ============================================================
// Confirmation Screen — review before launching
// ============================================================
const ConfirmationScreen = ({ dispatch }) => {
  const queue = useSelector(selectPublishQueue);

  const handleBack = () => {
    dispatch(setPublishPhase(PUBLISH_PHASES.SELECTING));
  };

  const handleStartPublishing = () => {
    dispatch(runPublishPipeline());
  };

  return (
    <Segment>
      <Header as="h3" icon="list" content="Confirm Publishing" />
      <Message warning>
        <Message.Header>Ready to publish {queue.length} draft{queue.length !== 1 ? 's' : ''}?</Message.Header>
        <p>
          This will create a new Literary Group for each draft below. PDFs and cover images
          will be copied from your draft library. This process may take several minutes.
        </p>
      </Message>

      <Table compact celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>#</Table.HeaderCell>
            <Table.HeaderCell width={5}>Literary Group Name</Table.HeaderCell>
            <Table.HeaderCell width={3}>Author</Table.HeaderCell>
            <Table.HeaderCell width={2}>Genre</Table.HeaderCell>
            <Table.HeaderCell width={2}>Books</Table.HeaderCell>
            <Table.HeaderCell width={2}>Covers</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {queue.map((item, index) => (
            <Table.Row key={item.uuid}>
              <Table.Cell>{index + 1}</Table.Cell>
              <Table.Cell><strong>{item.name}</strong></Table.Cell>
              <Table.Cell>{item.authorName || '—'}</Table.Cell>
              <Table.Cell>{item.genre || '—'}</Table.Cell>
              <Table.Cell>{item.bookCount}</Table.Cell>
              <Table.Cell>{item.coversCount}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <div style={{ marginTop: '1em' }}>
        <Button onClick={handleBack} icon="arrow left" labelPosition="left" content="Back" />
        <Button
          color="green"
          onClick={handleStartPublishing}
          icon="play"
          labelPosition="right"
          content="Start Publishing"
        />
      </div>
    </Segment>
  );
};

// ============================================================
// Processing Dashboard — shows live progress
// ============================================================
const ProcessingDashboard = ({ dispatch }) => {
  const phase = useSelector(selectPublishPhase);
  const queue = useSelector(selectPublishQueue);
  const progress = useSelector(selectPublishProgress);
  const isPaused = useSelector(selectIsPaused);
  const error = useSelector(selectPublishError);

  const isActive = phase === PUBLISH_PHASES.PUBLISHING;
  const isComplete = phase === PUBLISH_PHASES.COMPLETE;

  const statusIcon = (status) => {
    switch (status) {
      case ITEM_STATUS.QUEUED: return <Icon className="loading-icon" name="clock" color="grey" />;
      case ITEM_STATUS.CREATING_LITGROUP: return <Icon className="loading-icon" name="plus" color="blue" loading />;
      case ITEM_STATUS.COPYING_FILES: return <Icon className="loading-icon" name="copy" color="blue" loading />;
      case ITEM_STATUS.PROCESSING_PDFS: return <Icon className="loading-icon" name="cog" color="orange" loading />;
      case ITEM_STATUS.EXTRACTING_CHARACTERS: return <Icon className="loading-icon" name="users" color="purple" loading />;
      case ITEM_STATUS.COMPLETE: return <Icon className="loading-icon" name="check circle" color="green" />;
      case ITEM_STATUS.ERROR: return <Icon className="loading-icon" name="exclamation circle" color="red" />;
      case ITEM_STATUS.CANCELLED: return <Icon className="loading-icon" name="ban" color="grey" />;
      default: return <Icon name="question" />;
    }
  };

  const handleReset = () => {
    dispatch(resetPublishing());
  };

  return (
    <Segment>
      <Header as="h3" icon="cogs" content="Publishing Progress" />

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Progress
        percent={progress.overallPercent || 0}
        active={isActive && !isPaused}
        indicating
        progress
      >
        {isPaused ? '⏸ Paused' : (progress.currentStep || 'Waiting...')}
      </Progress>

      {/* Controls */}
      {isActive && (
        <div style={{ marginBottom: '1em' }}>
          {isPaused ? (
            <Button color="green" onClick={() => dispatch(resumePublishing())} icon="play" content="Resume" />
          ) : (
            <Button color="yellow" onClick={() => dispatch(pausePublishing())} icon="pause" content="Pause" />
          )}
          <Button color="red" onClick={() => dispatch(cancelPublishing())} icon="stop" content="Cancel" />
        </div>
      )}

      <Table compact="very" size="small" celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>Status</Table.HeaderCell>
            <Table.HeaderCell width={4}>Literary Group</Table.HeaderCell>
            <Table.HeaderCell width={3}>Author</Table.HeaderCell>
            <Table.HeaderCell width={2}>Books</Table.HeaderCell>
            <Table.HeaderCell width={3}>Phase</Table.HeaderCell>
            <Table.HeaderCell width={3}>Progress</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {queue.map((item, index) => (
            <Table.Row
              key={item.uuid}
              positive={item.status === ITEM_STATUS.COMPLETE}
              negative={item.status === ITEM_STATUS.ERROR}
            >
              <Table.Cell>{statusIcon(item.status)}</Table.Cell>
              <Table.Cell><strong>{item.name}</strong></Table.Cell>
              <Table.Cell>{item.authorName || '—'}</Table.Cell>
              <Table.Cell>{item.bookCount}</Table.Cell>
              <Table.Cell>
                <Label size="tiny" color={item.status === ITEM_STATUS.COMPLETE ? 'green' : item.status === ITEM_STATUS.ERROR ? 'red' : 'grey'}>
                  {item.status}
                </Label>
              </Table.Cell>
              <Table.Cell>
                <Progress
                  percent={item.progress || 0}
                  size="tiny"
                  active={[ITEM_STATUS.CREATING_LITGROUP, ITEM_STATUS.COPYING_FILES, ITEM_STATUS.PROCESSING_PDFS, ITEM_STATUS.EXTRACTING_CHARACTERS].includes(item.status)}
                  success={item.status === ITEM_STATUS.COMPLETE}
                  error={item.status === ITEM_STATUS.ERROR}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* Activity Log */}
      {progress.messages.length > 0 && (
        <Segment style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <Header as="h5">Activity Log</Header>
          {progress.messages.map((msg, idx) => (
            <div key={idx} style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
              <span style={{ color: '#888' }}>{new Date(msg.timestamp).toLocaleTimeString()}</span>{' '}
              {msg.message}
            </div>
          ))}
        </Segment>
      )}

      {/* Completion */}
      {isComplete && (
        <div style={{ marginTop: '1em' }}>
          <Message positive>
            <Message.Header>Publishing Complete!</Message.Header>
            <p>
              {queue.filter(q => q.status === ITEM_STATUS.COMPLETE).length} of {queue.length} Literary Group(s)
              published successfully.
            </p>
            {queue.filter(q => q.status === ITEM_STATUS.ERROR).length > 0 && (
              <p style={{ color: '#db2828' }}>
                {queue.filter(q => q.status === ITEM_STATUS.ERROR).length} Literary Group(s) had errors — check the activity log.
              </p>
            )}
          </Message>

          {/* Pipeline Summary */}
          <Message info size="small">
            <Message.Header>Phase 2 Pipeline Summary</Message.Header>
            <Message.List>
              <Message.Item>S3 files copied (PDFs, covers, author images)</Message.Item>
              <Message.Item>PDFs processed through OpenAI character/emotion extraction</Message.Item>
              <Message.Item>Characters and Variations imported into Neo4j</Message.Item>
              <Message.Item>CharSpeechSamples created for voice recording</Message.Item>
              <Message.Item>Opportunities created for each character</Message.Item>
            </Message.List>
            <p style={{ marginTop: '0.5em', fontSize: '0.9em', color: '#555' }}>
              Check the activity log above for per-item details.
            </p>
          </Message>

          <Button primary onClick={handleReset} icon="redo" content="Publish More" />
          <Button as={Link} to="/landing/book-creator" icon="home" content="Back to Dashboard" />
        </div>
      )}
    </Segment>
  );
};

// ============================================================
// Main Page Component
// ============================================================
const PublishPrototypesPage = () => {
  const dispatch = useDispatch();
  const phase = useSelector(selectPublishPhase);

  const handleReset = useCallback(() => {
    dispatch(resetPublishing());
  }, [dispatch]);

  const renderContent = () => {
    switch (phase) {
      case PUBLISH_PHASES.SELECTING:
        return <PrototypeSelectionScreen dispatch={dispatch} />;

      case PUBLISH_PHASES.CONFIRMING:
        return <ConfirmationScreen dispatch={dispatch} />;

      case PUBLISH_PHASES.PUBLISHING:
      case PUBLISH_PHASES.COMPLETE:
      case PUBLISH_PHASES.ERROR:
        return <ProcessingDashboard dispatch={dispatch} />;

      default:
        return <PrototypeSelectionScreen dispatch={dispatch} />;
    }
  };

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '2em' }}>
      {/* Page Header */}
      <Segment clearing>
        <Header as="h2" floated="left">
          <img src={lightening} alt="" />
          <Header.Content>
            Publish Literary Groups
            <Header.Subheader>Create LitGroups from your draft library</Header.Subheader>
          </Header.Content>
        </Header>
        <Button
          as={Link}
          to="/landing/book-creator"
          floated="right"
          icon="arrow left"
          content="Back"
          basic
        />
        {phase !== PUBLISH_PHASES.SELECTING && phase !== PUBLISH_PHASES.PUBLISHING && (
          <Button
            floated="right"
            icon="redo"
            content="Reset"
            basic
            color="red"
            onClick={handleReset}
          />
        )}
      </Segment>

      <Divider hidden />

      {/* Main Content */}
      {renderContent()}

      <AuthStdFooter />
    </Container>
  );
};

export default PublishPrototypesPage;
